export const ROLES = [
  "admin",
  "manager",
  "cashier",
  "kitchen",
  "inventory",
  "order-taker",
] as const;

export type Role = (typeof ROLES)[number];

export const PERMISSIONS = [
  "dashboard.view",
  "menu.view",
  "menu.manage",
  "orders.view",
  "orders.create",
  "orders.manage",
  "orders.delete",
  "orders.open",
  "reservations.view",
  "kitchen.view",
  "kitchen.manage",
  "inventory.view",
  "inventory.manage",
  "inventory.delete",
  "inventory.departments.delete",
  "crockery.view",
  "crockery.manage",
  "recipes.view",
  "recipes.manage",
  "recipes.delete",
  "staff.view",
  "staff.manage",
  "vendors.view",
  "vendors.manage",
  "vendors.delete",
  "transactions.view",
  "transactions.manage",
  "expenses.view",
  "expenses.manage",
  "roles.manage",
  "reports.view",
  "reports.manage",
  "activity.view",
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_STYLE: Record<Role, string> = {
  admin: "bg-orange-500/15 text-orange-400 border-orange-500/25",
  manager: "bg-blue-500/15 text-blue-400 border-blue-500/25",
  cashier: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25",
  kitchen: "bg-red-500/15 text-red-400 border-red-500/25",
  inventory: "bg-purple-500/15 text-purple-400 border-purple-500/25",
  "order-taker": "bg-cyan-500/15 text-cyan-400 border-cyan-500/25",
};

export const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  manager: "Manager",
  cashier: "Cashier",
  kitchen: "Kitchen",
  inventory: "Inventory",
  "order-taker": "Order Taker",
};

/** Default permissions per role — used to seed DB; runtime uses DB via rbac service. */
export const DEFAULT_ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  admin: PERMISSIONS,
  manager: [
    "dashboard.view",
    "menu.view",
    "menu.manage",
    "orders.view",
    "orders.create",
    "orders.manage",
    "orders.delete",
    "orders.open",
    "reservations.view",
    "kitchen.view",
    "kitchen.manage",
    "inventory.view",
    "inventory.manage",
    "inventory.delete",
    "crockery.view",
    "crockery.manage",
    "recipes.view",
    "recipes.manage",
    "recipes.delete",
    "staff.view",
    "vendors.view",
    "vendors.manage",
    "vendors.delete",
    "transactions.view",
    "transactions.manage",
    "expenses.view",
    "expenses.manage",
    "reports.view",
    "reports.manage",
    "activity.view",
  ],
  cashier: [
    "dashboard.view",
    "menu.view",
    "orders.view",
    "orders.create",
    "orders.manage",
    "reservations.view",
    "transactions.view",
  ],
  kitchen: ["kitchen.view", "kitchen.manage", "orders.view", "recipes.view"],
  /** Can manage stock/recipes/vendors day-to-day, but cannot delete (admin/manager only). */
  inventory: [
    "inventory.view",
    "inventory.manage",
    "crockery.view",
    "crockery.manage",
    "recipes.view",
    "recipes.manage",
    "vendors.view",
    "vendors.manage",
    "reports.view",
  ],
  "order-taker": ["menu.view", "orders.view", "orders.create", "reservations.view"],
};

/**
 * Optional allow-list of report slugs per role.
 * Roles not listed here see the full enabled catalog when they have reports.view.
 */
export const ROLE_ALLOWED_REPORTS: Partial<Record<Role, readonly string[]>> = {
  inventory: ["department-inventory-issues", "crockery-adjustment-log"],
};

export function canRoleAccessReport(role: string, reportSlug: string): boolean {
  if (role === "admin") return true;
  if (!isRole(role)) return true;
  const allowed = ROLE_ALLOWED_REPORTS[role];
  if (!allowed) return true;
  return allowed.includes(reportSlug);
}

/** @deprecated Use DEFAULT_ROLE_PERMISSIONS — kept as alias for existing imports */
export const ROLE_PERMISSIONS = DEFAULT_ROLE_PERMISSIONS;

export const ROUTE_PERMISSIONS: Record<string, Permission> = {
  "/dashboard": "dashboard.view",
  "/menu": "menu.view",
  "/pos": "orders.create",
  "/order-list": "orders.view",
  "/reservation-orders": "reservations.view",
  "/kitchen": "kitchen.view",
  "/inventory": "inventory.view",
  "/crockery": "crockery.view",
  "/recipes": "recipes.view",
  "/staff": "staff.view",
  "/vendors": "vendors.view",
  "/transactions": "transactions.view",
  "/expenses": "expenses.view",
  "/reports": "reports.view",
  "/activity-logs": "activity.view",
};

/** Nav items shown in the sidebar (label + path + required permission). */
export const NAV_ITEMS = [
  {
    label: "Dashboard",
    href: "/dashboard",
    permission: "dashboard.view" as Permission,
  },
  { label: "Menu", href: "/menu", permission: "menu.view" as Permission },
  {
    label: "POS",
    href: "/pos",
    permission: "orders.create" as Permission,
  },
  {
    label: "Order List",
    href: "/order-list",
    permission: "orders.view" as Permission,
  },
  {
    label: "Reservation Orders",
    href: "/reservation-orders",
    permission: "reservations.view" as Permission,
  },
  {
    label: "Kitchen",
    href: "/kitchen",
    permission: "kitchen.view" as Permission,
  },
  {
    label: "Inventory",
    href: "/inventory",
    permission: "inventory.view" as Permission,
  },
  {
    label: "Crockery",
    href: "/crockery",
    permission: "crockery.view" as Permission,
  },
  {
    label: "Recipes",
    href: "/recipes",
    permission: "recipes.view" as Permission,
  },
  { label: "Staff", href: "/staff", permission: "staff.view" as Permission },
  {
    label: "Vendors",
    href: "/vendors",
    permission: "vendors.view" as Permission,
  },
  {
    label: "Transactions",
    href: "/transactions",
    permission: "transactions.view" as Permission,
  },
  {
    label: "Expenses",
    href: "/expenses",
    permission: "expenses.view" as Permission,
  },
  {
    label: "Reports",
    href: "/reports",
    permission: "reports.view" as Permission,
  },
  {
    label: "Activity Logs",
    href: "/activity-logs",
    permission: "activity.view" as Permission,
  },
];

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export function isPermission(value: string): value is Permission {
  return (PERMISSIONS as readonly string[]).includes(value);
}

/** Static fallback when DB permissions are not loaded (e.g. tests). */
export function getDefaultPermissionsForRole(role: string): Permission[] {
  if (role === "admin") return [...PERMISSIONS];
  if (!isRole(role)) return [];
  return [...DEFAULT_ROLE_PERMISSIONS[role]];
}

export function hasPermissionInList(
  permissions: readonly Permission[],
  permission: Permission,
): boolean {
  return permissions.includes(permission);
}

export function hasAnyPermissionInList(
  permissions: readonly Permission[],
  required: Permission[],
): boolean {
  return required.some((p) => permissions.includes(p));
}

export function canAccessRouteWithPermissions(
  permissions: readonly Permission[],
  path: string,
): boolean {
  const base =
    Object.keys(ROUTE_PERMISSIONS)
      .filter((route) => path === route || path.startsWith(`${route}/`))
      .sort((a, b) => b.length - a.length)[0] ?? null;

  if (!base) return true;
  const permission = ROUTE_PERMISSIONS[base];
  return hasPermissionInList(permissions, permission);
}

export function getDefaultRouteForPermissions(
  permissions: readonly Permission[],
): string {
  const first = NAV_ITEMS.find((item) =>
    hasPermissionInList(permissions, item.permission),
  );
  return first?.href ?? "/login";
}

/** @deprecated Prefer permissions array from /api/auth/me */
export function getPermissionsForRole(role: string): Permission[] {
  return getDefaultPermissionsForRole(role);
}

/** @deprecated Prefer hasPermissionInList with session permissions */
export function hasPermission(role: string, permission: Permission): boolean {
  return hasPermissionInList(getDefaultPermissionsForRole(role), permission);
}

export function hasAnyPermission(
  role: string,
  permissions: Permission[],
): boolean {
  return hasAnyPermissionInList(
    getDefaultPermissionsForRole(role),
    permissions,
  );
}

export function canAccessRoute(role: string, path: string): boolean {
  return canAccessRouteWithPermissions(
    getDefaultPermissionsForRole(role),
    path,
  );
}

export function getDefaultRouteForRole(role: string): string {
  return getDefaultRouteForPermissions(getDefaultPermissionsForRole(role));
}

/** Human-readable permission labels for the staff permissions matrix. */
export const PERMISSION_LABELS: Record<Permission, string> = {
  "dashboard.view": "View dashboard",
  "menu.view": "View menu",
  "menu.manage": "Manage menu",
  "orders.view": "View orders",
  "orders.create": "Create orders",
  "orders.manage": "Manage orders & payments",
  "orders.delete": "Delete orders",
  "orders.open": "Create, edit & report open orders",
  "reservations.view": "View reservation orders",
  "kitchen.view": "View kitchen board",
  "kitchen.manage": "Update kitchen orders",
  "inventory.view": "View inventory",
  "inventory.manage": "Manage inventory",
  "inventory.delete": "Delete inventory items",
  "inventory.departments.delete": "Delete kitchen departments",
  "crockery.view": "View crockery",
  "crockery.manage": "Manage crockery",
  "recipes.view": "View recipes",
  "recipes.manage": "Manage recipes",
  "recipes.delete": "Delete recipes",
  "staff.view": "View staff",
  "staff.manage": "Manage staff",
  "vendors.view": "View vendors",
  "vendors.manage": "Manage vendors",
  "vendors.delete": "Delete vendors",
  "transactions.view": "View transactions",
  "transactions.manage": "Manage transactions",
  "expenses.view": "View expenses",
  "expenses.manage": "Manage expenses",
  "roles.manage": "Manage roles & permissions",
  "reports.view": "View sales & finance reports",
  "reports.manage": "Manage report catalog (add, edit, enable)",
  "activity.view": "View activity logs",
};
